import { CUSTOMER_LOGOS, LogoMarquee } from "@repo/design-system/components/logos";
import Image from "next/image";
import type { ReactNode } from "react";

export interface AuthTestimonialCopy {
  quote: string;
  author: string;
  role: string;
  integrationConnect: string;
  integrationDetail: string;
  logosHeading: string;
}

interface AuthSplitLayoutProps {
  /** Which painting the proof side shows; every page names its own. */
  painting: AuthPainting;
  headerRight: ReactNode;
  children: ReactNode;
  rightPanel?: ReactNode;
  testimonial?: AuthTestimonialCopy;
}

// Fallback copy for any consumer that hasn't threaded the localized dictionary
// through yet. Kept in English since it mirrors the source strings in
// @repo/internationalization/dictionaries/en.json#auth.testimonial.
const DEFAULT_TESTIMONIAL: AuthTestimonialCopy = {
  quote: "When I have an ongoing consultation I'm not able to answer the phone. There's peace of mind in knowing that the phone is still answered.",
  author: "Michela",
  role: "Owner, Visage Studio",
  integrationConnect: "connected through",
  integrationDetail: "through Verbu, and today Verbu books appointments whenever Michela isn't available and when customers write online.",
  logosHeading: "Other companies using Verbu include",
};

const AUTHOR_PHOTO = "/images/michela.png";
const BRAND_SEAL = { src: "/images/logos/visage-studio.webp", width: 380, height: 380 };
const GECKO_LOGO = { src: "/images/logos/geckobooking.png", width: 128, height: 128 };

// The same painting the onboarding welcome screen opens with, so the brand
// reads the same from the first screen to the survey. Decorative, so it carries
// an empty alt.
/**
 * One painting per auth page, none of them the windmill the onboarding survey
 * uses (Deani, 2026-09-06). Files live under apps/app/public/onboarding.
 */
export const AUTH_PAINTINGS = {
  signIn: "/onboarding/07.jpg",
  signUp: "/onboarding/05.jpg",
  partner: "/onboarding/06.jpg",
  resetPassword: "/onboarding/01.jpg",
  acceptInvitation: "/onboarding/02.jpg",
} as const;

export type AuthPainting = keyof typeof AUTH_PAINTINGS;

/**
 * The painting and the wash the proof side of every auth page stands on.
 *
 * Black and white on purpose rather than theme tokens: the painting does not
 * change with the theme, so neither does the text laid over it. The 30% wash is
 * the same one the onboarding welcome screen uses.
 */
export function AuthPaintingBackdrop({ painting }: { painting: AuthPainting }) {
  return (
    <>
      <Image alt="" className="object-cover" fill priority sizes="50vw" src={AUTH_PAINTINGS[painting]} />
      <div aria-hidden className="absolute inset-0 bg-black/30" />
    </>
  );
}

function AuthTestimonialPanel({ testimonial = DEFAULT_TESTIMONIAL, painting }: { testimonial?: AuthTestimonialCopy; painting: AuthPainting }) {
  return (
    <div className="relative hidden h-full flex-col overflow-hidden px-panel-x py-panel-y text-white lg:flex">
      <AuthPaintingBackdrop painting={painting} />

      <figure className="relative flex flex-1 flex-col items-center justify-center gap-stack text-center">
        {/* Author headshot with the brand seal as a corner badge */}
        <div className="relative h-32 w-32">
          <Image
            alt={testimonial.author}
            className="h-32 w-32 rounded-full object-cover ring-2 ring-white/40"
            height={256}
            src={AUTHOR_PHOTO}
            width={256}
          />
          <div className="-bottom-1 -right-1 absolute h-14 w-14 overflow-hidden rounded-full bg-white p-1 ring-2 ring-white/40">
            <Image
              alt="Visage Studio"
              className="h-full w-full object-contain"
              height={BRAND_SEAL.height}
              src={BRAND_SEAL.src}
              width={BRAND_SEAL.width}
            />
          </div>
        </div>

        {/* Integration highlight: Visage Studio to Gecko Booking through Verbu */}
        <div className="max-w-sm rounded-card bg-white/10 px-inset-lg py-inset text-left text-body ring-1 ring-white/20 backdrop-blur-sm">
          <span className="text-h4">Visage Studio</span> {testimonial.integrationConnect}{" "}
          <span className="mx-0.5 inline-flex h-5 w-5 translate-y-1 items-center justify-center overflow-hidden rounded bg-white p-0.5 align-baseline">
            <Image alt="" className="h-full w-full object-contain" height={GECKO_LOGO.height} src={GECKO_LOGO.src} width={GECKO_LOGO.width} />
          </span>{" "}
          <span className="text-h4">Gecko Booking</span> {testimonial.integrationDetail}
        </div>

        <blockquote className="max-w-md font-serif text-overlay">"{testimonial.quote}"</blockquote>

        <figcaption className="text-body text-white/80">
          <div className="text-h4 text-white">{testimonial.author}</div>
          <div>{testimonial.role}</div>
        </figcaption>
      </figure>

      {/* Customer logo marquee, the logos inverted to white so they read on the painting under the wash */}
      <div className="relative pt-section">
        <p className="pb-inset-lg text-center text-h3">{testimonial.logosHeading}</p>
        <LogoMarquee imageClassName="contrast-150 grayscale invert" logos={CUSTOMER_LOGOS} />
      </div>
    </div>
  );
}

export function AuthSplitLayout({ headerRight, children, rightPanel, testimonial, painting }: AuthSplitLayoutProps) {
  return (
    <div className="shell grid min-h-screen grid-cols-1 bg-canvas lg:grid-cols-2">
      <div className="flex items-center justify-center p-gutter">
        <div className="w-full max-w-md rounded-card bg-card px-panel-x py-panel-y shadow-raised ring-1 ring-border">
          <div className="flex flex-col gap-section">
            <div className="flex items-center justify-between">
              <img alt="Verbu" className="h-7 w-auto dark:hidden" height={17} src="/images/logo/logo-full.svg" width={62} />
              <img alt="Verbu" className="hidden h-7 w-auto dark:block" height={17} src="/images/logo/logo-white.svg" width={62} />
              {headerRight}
            </div>
            {children}
          </div>
        </div>
      </div>
      {rightPanel ?? <AuthTestimonialPanel painting={painting} testimonial={testimonial} />}
    </div>
  );
}
